import {
  ChakraBox,
  ChakraFlex,
  EmptySectionCard,
  FAIconName,
  Text,
  FireflyButton,
  SkeletonLoader,
} from '@equip.health/ui';
import { useState, useEffect } from 'react';

import { OrderLabDrawer } from './OrderLabDrawer';
import {
  LAB_TAB_HEADING,
  NO_LABS_ORDERED_MESSAGE,
  ORDER_LAB_TEXT,
} from '~/lib/constants/patients.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { shouldShowOrderLabButton } from '~/lib/util/patient.util';
import { useGetPatientChartUrlQuery } from '~/store/services/labs';
import LabOrderActionBar from '../../common/LabOrderActionBar';
import { getEmptyLabOrderFields } from '~/lib/util/lab.util';
import { useLocation, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '~/store/hooks';
import { setShowOrders } from '~/store/reducers/patientLabTab';
import { PatientEvents } from '~/analytics/patientEvents';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

interface PatientLabsTabProps {
  diagnosticReportId?: string;
  patient: Patient;
}
const PatientLabsTab = ({ diagnosticReportId: diagnosticReportIdProp, patient }: PatientLabsTabProps) => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { trackEvent } = useAnalytics();
  const diagnosticReportId = diagnosticReportIdProp ?? (params[0] ? params[0].split('/')[2] : '');
  const [showOrderLabDrawer, setShowOrderLabDrawer] = useState<boolean>(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState<boolean>(false);
  const [showActionBar, setShowActionBar] = useState<boolean>(false);
  const { currentUser } = useGlobalContext();
  const patientId = patient.userId;
  const TOP_LEVEL_ID = 'patient-lab';
  const shouldShowOrders: boolean = useAppSelector(
    (state) => state?.patientLabTab,
  );
  const { data: patientChartUrl, isLoading } = useGetPatientChartUrlQuery(
    {
      diagnosticReportId,
      patientExternalId: patientId,
      ...(shouldShowOrders && { showOrders: shouldShowOrders }),
    },
    { refetchOnMountOrArgChange: !!diagnosticReportId || shouldShowOrders },
  );

  useEffect(() => {
    return () => {
      const leavingPatientLabsTab = !location.pathname.includes('/labs');
      if (shouldShowOrders && leavingPatientLabsTab) {
        dispatch(setShowOrders(false));
      }
    };
  }, [location.pathname, shouldShowOrders]);

  const handleLabOrderSubmitted = () => {
    dispatch(setShowOrders(true));
    setShowActionBar(true);
    setTimeout(() => setShowActionBar(false), 8000);
  };
  const handleIframeLoad = () => {
    if (patientChartUrl?.url) {
      setIsIframeLoaded(true);
    }
  };

  return (
    <>
      <SkeletonLoader
        height="100%"
        id={`${TOP_LEVEL_ID}__skeleton-loader`}
        isLoaded={isIframeLoaded || !isLoading}
      >
        {patientChartUrl?.url ? (
          <ChakraBox height="100%" marginTop="10px">
            <iframe
              allowFullScreen
              data-testid="patient-chart"
              onLoad={handleIframeLoad}
              src={patientChartUrl.url}
              style={{ border: 'none', height: '100%', width: '100%' }}
              title="patient-chart"
            />
          </ChakraBox>
        ) : (
          <ChakraBox display="flex" flexDirection="column" gap="10px">
            <ChakraFlex
              alignItems="center"
              justifyContent="space-between"
              marginTop="12px"
            >
              {!patientChartUrl?.url && (
                <Text variant="h5">{LAB_TAB_HEADING}</Text>
              )}
              {shouldShowOrderLabButton(
                currentUser?.providerTypeName || '',
                patient.universalStatusCode,
              ) && (
                <FireflyButton
                  id={`${TOP_LEVEL_ID}__order-lab`}
                  leftIcon={FAIconName.plusRegular}
                  onClick={() => {
                    trackEvent(PatientEvents.orderLabLabsTabButtonClicked, {
                      Source: AnalyticsSourceValues.LABS_TAB,
                    });
                    setShowOrderLabDrawer(true);
                  }}
                  size="small"
                  variant="secondary"
                >
                  {ORDER_LAB_TEXT}
                </FireflyButton>
              )}
            </ChakraFlex>
            <EmptySectionCard
              caption={NO_LABS_ORDERED_MESSAGE}
              id={`${TOP_LEVEL_ID}__empty-card`}
            />
          </ChakraBox>
        )}
      </SkeletonLoader>
      {showOrderLabDrawer && (
        <OrderLabDrawer
          closeDrawer={() => setShowOrderLabDrawer(false)}
          emptyFields={getEmptyLabOrderFields(patient)}
          isOpen={showOrderLabDrawer}
          onLabOrderSubmitted={handleLabOrderSubmitted}
          patientId={patientId}
        />
      )}
      {showActionBar && (
        <LabOrderActionBar
          onHide={() => setShowActionBar(false)}
          patientId={patientId}
        />
      )}
    </>
  );
};

export default PatientLabsTab;
